import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from './Style.module.css';
import AdminService from '../../API/AdminService';
import logger from '../../utils/logger';
import PageIntro from '../../components/PageIntro/PageIntro';
import {
    IconSettings, IconBook, IconEvents, IconPlus, IconEdit, IconClose,
    IconArrowRight,
} from '../../components/Icons';

const Settings = () => {
    const navigate = useNavigate();
    const [showCourseModal, setShowCourseModal] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);
    const [notification, setNotification] = useState('');
    const [loading, setLoading] = useState(false);

    // Дни недели для выпадающего списка (для курсов)
    const daysOfWeek = [
        { value: '', label: 'Не выбрано' },
        { value: 1, label: 'Понедельник' },
        { value: 2, label: 'Вторник' },
        { value: 3, label: 'Среда' },
        { value: 4, label: 'Четверг' },
        { value: 5, label: 'Пятница' },
        { value: 6, label: 'Суббота' },
        { value: 0, label: 'Воскресенье' },
    ];

    // Дни недели для мероприятий (с опцией «Несколько»)
    const eventDaysOfWeek = [
        ...daysOfWeek,
        { value: -1, label: 'Несколько' },
    ];

    const [newCourse, setNewCourse] = useState({
        name: '', day: '', timeStart: '', timeEnd: '',
        dateStart: '', dateEnd: '', prepod: '',
    });

    const [newEvent, setNewEvent] = useState({
        name: '', day: '', timeStart: '', timeEnd: '',
        dateStart: '', dateEnd: '', prepod: '',
    });

    // Обработчики для курса
    const handleCourseInputChange = (e) => {
        const { name, value } = e.target;
        setNewCourse(prev => ({
            ...prev,
            [name]: name === 'day' ? (value === '' ? '' : Number(value)) : value,
        }));
    };

    const handleCourseSubmit = async (e) => {
        e.preventDefault();
        if (!newCourse.name) {
            showNotification('Заполните название курса', 'error');
            return;
        }
        setLoading(true);
        try {
            const response = await AdminService.createCourse({
                ...newCourse,
                status: 'Активный',
            });
            if (response) {
                showNotification('Курс успешно создан', 'success');
                setNewCourse({
                    name: '', day: '', timeStart: '', timeEnd: '',
                    dateStart: '', dateEnd: '', prepod: '',
                });
                setTimeout(() => setShowCourseModal(false), 1200);
            }
        } catch (error) {
            logger.error('Ошибка создания курса:', error);
            showNotification('Ошибка создания курса', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Обработчики для мероприятия
    const handleEventInputChange = (e) => {
        const { name, value } = e.target;
        setNewEvent(prev => ({
            ...prev,
            [name]: name === 'day' ? (value === '' ? '' : Number(value)) : value,
        }));
    };

    const handleEventSubmit = async (e) => {
        e.preventDefault();
        if (!newEvent.name) {
            showNotification('Заполните название мероприятия', 'error');
            return;
        }
        setLoading(true);
        try {
            const response = await AdminService.createEvent({
                ...newEvent,
                status: 'Активный',
            });
            if (response) {
                showNotification('Мероприятие создано', 'success');
                setNewEvent({
                    name: '', day: '', timeStart: '', timeEnd: '',
                    dateStart: '', dateEnd: '', prepod: '',
                });
                setTimeout(() => setShowEventModal(false), 1200);
            }
        } catch (error) {
            logger.error('Ошибка создания мероприятия:', error);
            showNotification('Ошибка создания мероприятия', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification(''), 3000);
    };

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Карточка-шапка */}
                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconSettings width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Настройки журнала</h1>
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Журнал · Настройки
                            </div>
                        </div>
                    </div>
                </div>

                <PageIntro
                    storageKey="jornal-settings"
                    title="О странице «Настройки журнала»"
                    text="Здесь вы заводите курсы и мероприятия, которые потом появятся в расписании. Курс — это регулярная группа учеников (одно и то же время каждую неделю). Мероприятие — разовая активность или серия (лагерь, мастер-класс, день открытых дверей)."
                    bullets={[
                        '«Добавить курс / мероприятие» — открывает форму с днём недели, временем, периодом и преподавателем',
                        '«Редактировать» — список существующих, с возможностью изменить или удалить',
                        'Изменения сразу подхватываются в расписании и журнале',
                    ]}
                />

                {/* Уведомления */}
                {notification && (
                    <div className={notification.type === 'success' ? cl.notification : cl.error}>
                        {notification.message}
                    </div>
                )}

                {/* Две группы по две карточки-действия */}
                <div className={cl.settingsGroups}>
                    {/* Курсы */}
                    <section className={cl.settingsSection}>
                        <div className={cl.settingsSectionHead}>
                            <span className={cl.settingsSectionIcon}>
                                <IconBook width={16} height={16}/>
                            </span>
                            <h2 className={cl.settingsSectionTitle}>Курсы</h2>
                        </div>
                        <div className={cl.settingsActions}>
                            <button
                                className={`${cl.settingsAction} ${cl.settingsActionPrimary}`}
                                onClick={() => setShowCourseModal(true)}
                            >
                                <span className={cl.settingsActionIcon}>
                                    <IconPlus width={18} height={18}/>
                                </span>
                                <span className={cl.settingsActionBody}>
                                    <span className={cl.settingsActionTitle}>Добавить курс</span>
                                    <span className={cl.settingsActionDesc}>
                                        Название, расписание и преподаватель
                                    </span>
                                </span>
                                <span className={cl.settingsActionArrow}>
                                    <IconArrowRight width={14} height={14}/>
                                </span>
                            </button>

                            <button
                                className={cl.settingsAction}
                                onClick={() => navigate('/main/edit-courses')}
                            >
                                <span className={cl.settingsActionIcon}>
                                    <IconEdit width={18} height={18}/>
                                </span>
                                <span className={cl.settingsActionBody}>
                                    <span className={cl.settingsActionTitle}>Редактировать курсы</span>
                                    <span className={cl.settingsActionDesc}>
                                        Изменить или удалить существующие
                                    </span>
                                </span>
                                <span className={cl.settingsActionArrow}>
                                    <IconArrowRight width={14} height={14}/>
                                </span>
                            </button>
                        </div>
                    </section>

                    {/* Мероприятия */}
                    <section className={cl.settingsSection}>
                        <div className={cl.settingsSectionHead}>
                            <span className={cl.settingsSectionIcon}>
                                <IconEvents width={16} height={16}/>
                            </span>
                            <h2 className={cl.settingsSectionTitle}>Мероприятия</h2>
                        </div>
                        <div className={cl.settingsActions}>
                            <button
                                className={`${cl.settingsAction} ${cl.settingsActionPrimary}`}
                                onClick={() => setShowEventModal(true)}
                            >
                                <span className={cl.settingsActionIcon}>
                                    <IconPlus width={18} height={18}/>
                                </span>
                                <span className={cl.settingsActionBody}>
                                    <span className={cl.settingsActionTitle}>Добавить мероприятие</span>
                                    <span className={cl.settingsActionDesc}>
                                        Разовое или серия (например, лагерь)
                                    </span>
                                </span>
                                <span className={cl.settingsActionArrow}>
                                    <IconArrowRight width={14} height={14}/>
                                </span>
                            </button>

                            <button
                                className={cl.settingsAction}
                                onClick={() => navigate('/main/edit-events')}
                            >
                                <span className={cl.settingsActionIcon}>
                                    <IconEdit width={18} height={18}/>
                                </span>
                                <span className={cl.settingsActionBody}>
                                    <span className={cl.settingsActionTitle}>Редактировать мероприятия</span>
                                    <span className={cl.settingsActionDesc}>
                                        Изменить или удалить существующие
                                    </span>
                                </span>
                                <span className={cl.settingsActionArrow}>
                                    <IconArrowRight width={14} height={14}/>
                                </span>
                            </button>
                        </div>
                    </section>
                </div>
            </div>

            {/* Модалка создания курса */}
            {showCourseModal && (
                <ModalForm
                    title="Добавить курс"
                    icon={<IconBook width={18} height={18}/>}
                    onClose={() => setShowCourseModal(false)}
                >
                    <form onSubmit={handleCourseSubmit} className={cl.modalForm}>
                        <div className={cl.modalFormRow}>
                            <Field label="Название курса *">
                                <input
                                    type="text"
                                    name="name"
                                    value={newCourse.name}
                                    onChange={handleCourseInputChange}
                                    className={cl.modalInput}
                                    placeholder="Например, Скретч 8–10 лет"
                                    required
                                />
                            </Field>
                            <Field label="День недели">
                                <select
                                    name="day"
                                    value={newCourse.day}
                                    onChange={handleCourseInputChange}
                                    className={cl.modalInput}
                                >
                                    {daysOfWeek.map(day => (
                                        <option key={day.value} value={day.value}>{day.label}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Время начала">
                                <input type="time" name="timeStart" value={newCourse.timeStart}
                                    onChange={handleCourseInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Время окончания">
                                <input type="time" name="timeEnd" value={newCourse.timeEnd}
                                    onChange={handleCourseInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Дата начала курса">
                                <input type="date" name="dateStart" value={newCourse.dateStart}
                                    onChange={handleCourseInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Дата окончания курса">
                                <input type="date" name="dateEnd" value={newCourse.dateEnd}
                                    onChange={handleCourseInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <Field label="Преподаватель *">
                            <input
                                type="text"
                                name="prepod"
                                value={newCourse.prepod}
                                onChange={handleCourseInputChange}
                                className={cl.modalInput}
                                placeholder="Иванов Иван Иванович"
                                required
                            />
                        </Field>

                        <div className={cl.modalActions}>
                            <button type="button" onClick={() => setShowCourseModal(false)} className={cl.btnSecondary}>
                                Отмена
                            </button>
                            <button type="submit" disabled={loading} className={cl.btnPrimary}>
                                {loading ? 'Создание…' : 'Создать курс'}
                            </button>
                        </div>
                    </form>
                </ModalForm>
            )}

            {/* Модалка создания мероприятия */}
            {showEventModal && (
                <ModalForm
                    title="Добавить мероприятие"
                    icon={<IconEvents width={18} height={18}/>}
                    onClose={() => setShowEventModal(false)}
                >
                    <form onSubmit={handleEventSubmit} className={cl.modalForm}>
                        <div className={cl.modalFormRow}>
                            <Field label="Название мероприятия *">
                                <input
                                    type="text"
                                    name="name"
                                    value={newEvent.name}
                                    onChange={handleEventInputChange}
                                    className={cl.modalInput}
                                    placeholder="Например, Летний лагерь"
                                    required
                                />
                            </Field>
                            <Field label="День недели">
                                <select
                                    name="day"
                                    value={newEvent.day}
                                    onChange={handleEventInputChange}
                                    className={cl.modalInput}
                                >
                                    {eventDaysOfWeek.map(day => (
                                        <option key={day.value} value={day.value}>{day.label}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Время начала">
                                <input type="time" name="timeStart" value={newEvent.timeStart}
                                    onChange={handleEventInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Время окончания">
                                <input type="time" name="timeEnd" value={newEvent.timeEnd}
                                    onChange={handleEventInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Дата начала">
                                <input type="date" name="dateStart" value={newEvent.dateStart}
                                    onChange={handleEventInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Дата окончания">
                                <input type="date" name="dateEnd" value={newEvent.dateEnd}
                                    onChange={handleEventInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <Field label="Преподаватель *">
                            <input
                                type="text"
                                name="prepod"
                                value={newEvent.prepod}
                                onChange={handleEventInputChange}
                                className={cl.modalInput}
                                placeholder="Иванов Иван Иванович"
                                required
                            />
                        </Field>

                        <div className={cl.modalActions}>
                            <button type="button" onClick={() => setShowEventModal(false)} className={cl.btnSecondary}>
                                Отмена
                            </button>
                            <button type="submit" disabled={loading} className={cl.btnPrimary}>
                                {loading ? 'Создание…' : 'Создать мероприятие'}
                            </button>
                        </div>
                    </form>
                </ModalForm>
            )}
        </div>
    );
};


// Универсальная модалка-форма в едином стиле сайта.
// Тёмный overlay, светлая карточка, иконка в заголовке.
function ModalForm({ title, icon, onClose, children }) {
    return (
        <div className={cl.modalOverlayV2} onClick={onClose}>
            <div className={cl.modalCardV2} onClick={e => e.stopPropagation()}>
                <header className={cl.modalCardHead}>
                    <div className={cl.modalCardTitleWrap}>
                        <span className={cl.modalCardIcon}>{icon}</span>
                        <h2 className={cl.modalCardTitle}>{title}</h2>
                    </div>
                    <button className={cl.modalCardClose} onClick={onClose} aria-label="Закрыть">
                        <IconClose width={18} height={18}/>
                    </button>
                </header>
                <div className={cl.modalCardBody}>
                    {children}
                </div>
            </div>
        </div>
    );
}

// Поле формы с лейблом
function Field({ label, children }) {
    return (
        <div className={cl.modalField}>
            <label className={cl.modalLabel}>{label}</label>
            {children}
        </div>
    );
}

export default Settings;
